// src/pages/FavoritePartsSection.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { API_BASE_URL } from "../utils/api";
import useIsMobile from "../hooks/useIsMobile";

// Reuse your parts step UIs
import PartsStepMobile from "./PartsStepMobile";
import PartsStepDesktop from "./PartsStepDesktop";
import SelectableCard from "./SelectableCard";

import "../styles/Favorites.css";
import "../styles/BrandsSection.css";

export default function FavoritePartsSection({ onOpenPartById, setCurrentView }) {
  const token = localStorage.getItem("token");
  const isMobile = useIsMobile(768);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(!!token);
  const [error, setError] = useState("");

  // Paging config (Desktop uses 3-up in PartsStepDesktop, Mobile 4/page to match your parts grid)
  const DESKTOP_PAGE_SIZE = 3;
  const MOBILE_PAGE_SIZE = 4;

  const [page, setPage] = useState(0);

  // Fetch favorites
  useEffect(() => {
    if (!token) {
      setItems([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError("");

    axios
      .get(`${API_BASE_URL}/wishlist`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (cancelled) return;
        const data = Array.isArray(res?.data) ? res.data : res?.data?.data || [];
        setItems(data);
        setPage(0);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error(err);
        setError("تعذر جلب المفضلة الآن.");
      })
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [token]);

  

  // Pagination calculations
  const pageSize = isMobile ? MOBILE_PAGE_SIZE : DESKTOP_PAGE_SIZE;
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const paginatedList = useMemo(() => {
    const start = page * pageSize;
    return items.slice(start, start + pageSize);
  }, [items, page, pageSize]);

  const canPrev = page > 0;
  const canNext = page < totalPages - 1;

  const setCurrentPage = useCallback((fnOrVal) => {
    setPage((prev) => (typeof fnOrVal === "function" ? fnOrVal(prev) : fnOrVal));
  }, []);

  // RTL detection for swipe
  const [isRtl, setIsRtl] = useState(false);
  useEffect(() => {
    if (typeof document !== "undefined") {
      setIsRtl(document.documentElement.dir === "rtl" || document.body.dir === "rtl");
    }
  }, []);

  // Swipe handler (mirrors your controller logic)
  const handlePartsMobileSwipeEnd = (_, info) => {
    const { offset, velocity } = info;
    const dx = offset.x;
    const vx = velocity.x;
    const power = Math.abs(dx) + Math.abs(vx) * 0.2;
    const THRESH = 150;
    const nextByDir = isRtl ? dx > 0 || vx > 300 : dx < 0 || vx < -300;
    const prevByDir = isRtl ? dx < 0 || vx < -300 : dx > 0 || vx > 300;
    if (power > THRESH) {
      if (nextByDir && canNext) {
        setCurrentPage((p) => Math.min(totalPages - 1, p + 1));
        return;
      }
      if (prevByDir && canPrev) {
        setCurrentPage((p) => Math.max(0, p - 1));
      }
    }
  };

  // Open details via your App “router”
  const onPartSelect = (id) => {
    if (onOpenPartById) {
      onOpenPartById(id);
    } else {
      // Fallback: navigate by URL (works with the simple router you set up)
      const path = `/part/${id}`;
      if (path !== window.location.pathname) {
        window.history.pushState({}, "", path);
        window.dispatchEvent(new PopStateEvent("popstate"));
      }
    }
  };

  return (
    <section className="fav-wrap" dir="rtl">
      <div className="clip-bg" aria-hidden="true" />
      <h1 className="fav-title">القطع المفضلة</h1>

      {!token && (
        <div className="fav-state warn">
          <a
            href=""
            onClick={(e) => { e.preventDefault(); setCurrentView?.("login"); }}
            style={{ color: "#CAEA3E", textDecoration: "underline", cursor: "pointer" }}
          >
             سجل دخول عشان تقدر تشوف قطعك المفضلة
          </a>
         
        </div>
      )}

      {token && loading && <div className="fav-state">جاري التحميل…</div>}
      {token && !loading && error && <div className="fav-state error">{error}</div>}
      {token && !loading && !error && items.length === 0 && (
        <div className="fav-state">لا توجد قطع مفضلة حالياً.</div>
      )}

      {token && !loading && !error && items.length > 0 && (
        isMobile ? (
          <PartsStepMobile
            items={paginatedList}
            currentPage={page}
            totalPages={totalPages}
            canPrev={canPrev}
            canNext={canNext}
            setCurrentPage={setCurrentPage}
            onSwipeEnd={handlePartsMobileSwipeEnd}
            onPartSelect={onPartSelect}
            showSuggest={false} // no "Other/Suggest" bar on favorites
            // Use your standard part card with an override
            CardComponent={SelectableCard}
            cardPropsGetter={() => ({
              isPartOverride: true,   // force card to render as "part"
              isMobile: true, // optional: suppress struck-through market price on mobile
            })}
          />
        ) : (
          <PartsStepDesktop
            paginatedList={paginatedList}
            listLength={items.length}
            currentPage={page}
            setCurrentPage={setCurrentPage}
            onPartSelect={onPartSelect}
            showSuggest={false} // no "Other/Suggest" bar on favorites
            // Use your standard part card with an override
            CardComponent={SelectableCard}
            cardPropsGetter={() => ({
              isPartOverride: true,
              isMobile: false
            })}
          />
        )
      )}
    </section>
  );
}
